import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  useToast,
  Spinner,
  IconButton,
  Image,
  Tag,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { LuChevronRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { fetchAccounts } from '../../api/endpointsBackend';
import MovementsList from '../shared/MovementsList';
import MovementDetailPanel from '../movements/MovementDetailPanel';
import AccountBalanceChart from './AccountBalanceChart';
import AccountForm from './AccountForm';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { useUsdRates } from '../../hooks/useUsdRates';
import useIsPhone from '../../hooks/useIsPhone';
import useHoldingReturns from '../../hooks/useHoldingReturns';
import useOpenAccountMovements from '../../hooks/useOpenAccountMovements';
import { getRangeDateWindow } from '../../utils/movementBuckets';
import { returnRangeLabel } from '../../utils/holdingReturns';
import { PhoneAccountMovements, PhoneAccountSummary } from '../phone/PhoneAccountSections';
import {
  HoldingDetail, HoldingSummary, BrokerDetail, BrokerSummary, PersonDetail, LoanDetail,
  brokerHeadline, holdingHeadline,
} from './SubAccountDetail';
import Figure from '../shared/Figure';
import {
  balanceByCurrency, childrenOf, isHolding, isLoan,
} from '../../utils/subAccounts';
import { statementLine } from '../../utils/statementFreshness';

// In the phone's account sheet the chart's range and currency pills are taps,
// so they get a 44px target there; the desktop panel keeps its compact pills.
const PHONE_TARGETS = { 'button': { minHeight: '44px', minWidth: '44px' } };
// The phone sheet's side gutter; the desktop panel keeps its own.
const PANEL_PX = { base: 3, md: 5 };
// The panel shows the note's first words; the whole note is in Edit.
const NOTE_WORDS = 5;
const shortNote = (note) => {
  const words = note.trim().split(/\s+/);
  return words.length > NOTE_WORDS ? `${words.slice(0, NOTE_WORDS).join(' ')}…` : words.join(' ');
};

// `reconciliation` is the account's balance-vs-bank status. The panel's own
// account fetch does not carry it, so a caller that has it (the phone's
// Accounts list) passes it in, and the phone layout loads it otherwise; only
// the phone layout shows it.
// `onEditingChange` tells the page whether a form is on screen, so the
// resizable panel can narrow to the form's measure while one is.
// `onSelectAccount(id)` opens another account in the panel: a holding from its
// broker's table, a loan from its person, the holding a "Same as…" kept.
// `freshness` is the account's statement freshness, which the desktop header
// shows beside its type.
export default function AccountDetailPanel({
  accountId, onClose, onAccountUpdated, onEditingChange, currency,
  currencyTab = 'all', categories, allTags, buckets, roi, reconciliation, onSelectAccount, freshness,
  paidReturns = null,
}) {
  const { t } = useTranslation('accounts');
  const isPhone = useIsPhone();
  const isCreateMode = !accountId;

  // Currencies that actually have transaction data for this account
  const accountCurrencies = useMemo(() => {
    if (!accountId || !buckets) return currency ? [currency] : [];
    const set = new Set(buckets.filter(b => b.account_id === accountId).map(b => b.currency));
    const list = [...set].filter(Boolean);
    return list.length > 0 ? list : (currency ? [currency] : []);
  }, [accountId, buckets, currency]);

  const [chartCurrency, setChartCurrency] = useState(null);

  // Keep chart currency in sync with the global currency tab
  useEffect(() => {
    if (currency && accountCurrencies.includes(currency)) {
      setChartCurrency(currency);
    }
  }, [currency, accountCurrencies]);

  // MI-298 stock view: when the global display currency is active, the chart
  // sums all of this account's currencies at today's rate (per-currency chips
  // are hidden — the single ≈ series replaces them).
  // The phone sheet stays in the account's own currencies, with their chips:
  // a card or row opens it on one of them, and the headline must be that
  // balance, not a sum of every currency the account holds.
  const { displayCurrency } = useDisplayCurrency();
  const { rates: usdRates } = useUsdRates();
  const chartConvert = useMemo(() => {
    if (isPhone || !displayCurrency || !usdRates?.[displayCurrency]) return null;
    return { target: displayCurrency, rates: usdRates };
  }, [isPhone, displayCurrency, usdRates]);
  const px = isPhone ? '16px' : PANEL_PX;
  const movementsFilters = useMemo(
    () => accountId ? [{ entity: 'Movement', column: 'account_id', operator: 'in', values: [accountId] }] : null,
    [accountId]
  );
  const [account, setAccount] = useState(null);
  // Every account, with positions and loans: a broker's holdings, a person's
  // loans and the pickers of their forms come from it.
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [movementsRefreshKey, setMovementsRefreshKey] = useState(0);
  const toast = useToast();
  const openAccountMovements = useOpenAccountMovements();

  // `silent` reloads in place, without the spinner (after a sub-account action).
  const fetchAccountData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const accounts = await fetchAccounts(null, { includePositions: true });
      const foundAccount = accounts.find(acc => acc.id === accountId);
      setAllAccounts(accounts);

      if (foundAccount) {
        setAccount(foundAccount);
      } else {
        setError(t('detail.notFound'));
      }
    } catch (err) {
      console.error('Error fetching account:', err);
      setError(t('detail.loadError'));
      toast({
        title: t('detail.toasts.errorTitle'),
        description: t('detail.loadError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [accountId, toast, t]);

  // Reset view/edit state and fetch data when the selected account changes
  useEffect(() => {
    setSelectedMovementId(null);
    setOverlayDirty(false);
    setChartCurrency(null);
    if (accountId) {
      setShowEditForm(false);
      fetchAccountData();
    } else {
      setAccount(null);
      setShowEditForm(true);
    }
  }, [accountId, fetchAccountData]);

  // The page sizes the panel to the form while it's showing one (MI-340): the
  // form is a narrow column, the account's detail is a chart plus its movements.
  const isShowingForm = showEditForm || isCreateMode;
  useEffect(() => {
    if (onEditingChange) onEditingChange(isShowingForm);
  }, [isShowingForm, onEditingChange]);

  // After AccountForm saves: create mode closes the panel; edit mode refreshes
  // the panel data and returns to view mode.
  const handleFormSaved = useCallback(async () => {
    if (onAccountUpdated) onAccountUpdated();
    if (isCreateMode) {
      if (onClose) onClose();
    } else {
      await fetchAccountData();
      setShowEditForm(false);
    }
  }, [isCreateMode, onAccountUpdated, onClose, fetchAccountData]);

  // After an action of a holding, broker, person or loan (a price, a new
  // loan, a payment…): reload the figures here, the movements and the list.
  const handleSubAccountChanged = useCallback(() => {
    fetchAccountData({ silent: true });
    setMovementsRefreshKey(k => k + 1);
    if (onAccountUpdated) onAccountUpdated();
  }, [fetchAccountData, onAccountUpdated]);

  const handleOverlayMovementUpdated = useCallback(() => {
    setOverlayDirty(true);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setSelectedMovementId(null);
    if (overlayDirty) {
      setMovementsRefreshKey(k => k + 1);
      setOverlayDirty(false);
      if (onAccountUpdated) onAccountUpdated();
    }
  }, [overlayDirty, onAccountUpdated]);

  // The holding, broker, person or loan block, above the balance chart. Its
  // actions show where the panel may edit (the phone sheet is read-only).
  const subAccountProps = {
    account,
    accounts: allAccounts,
    categories,
    canEdit: Boolean(onAccountUpdated),
    onChanged: handleSubAccountChanged,
    onListChanged: onAccountUpdated,
    onSelectAccount,
  };
  const renderSubAccount = () => {
    if (!account) return null;
    if (isHolding(account)) return <HoldingDetail {...subAccountProps} convert={chartConvert} paid={paid} />;
    if (isLoan(account)) return <LoanDetail account={account} />;
    if (account.parent_account_id != null) return null;
    if (account.account_type === 'lend') return <PersonDetail {...subAccountProps} />;
    return null;
  };

  // A broker shows what it holds today above its chart, and its holdings
  // below. The chart adds the holdings to the broker's cash at their latest
  // price on each day, so a buy is no drop and the line ends at the headline.
  const isBroker = Boolean(account) && account.parent_account_id == null && account.account_type === 'investment';
  const brokerHoldings = useMemo(
    () => (isBroker ? childrenOf(allAccounts, account.id).filter(isHolding) : []),
    [isBroker, allAccounts, account],
  );
  const brokerHoldingIds = useMemo(() => brokerHoldings.map(h => h.id), [brokerHoldings]);
  const showsHoldings = brokerHoldingIds.length > 0;
  // The chart asks again for the holdings' gains when a price or the units change.
  const marketVersion = brokerHoldings
    .map(h => `${h.id}:${h.position?.units}:${h.position?.price}:${h.position?.price_date}`).join('|');
  const atMarket = useMemo(
    () => (showsHoldings ? { accountId: account.id, version: marketVersion } : null),
    [showsHoldings, account, marketVersion],
  );
  const brokerCash = useMemo(
    () => (isBroker ? balanceByCurrency(buckets, account.id) : null),
    [isBroker, buckets, account],
  );
  const chartMembers = useMemo(
    () => (showsHoldings ? [account.id, ...brokerHoldingIds] : null),
    [showsHoldings, account, brokerHoldingIds],
  );
  // A holding heads with its own value and gain; its chart is what was put
  // in, at cost.
  const holdingView = Boolean(account) && isHolding(account);
  const ownHeadline = showsHoldings || holdingView;
  // A holding's or broker's return over the chart's range: in the display
  // currency when one is picked (each buy at its own day's rate), else in its
  // own. `ALL` with no display currency is the position it already carries.
  const [chartRange, setChartRange] = useState('ALL');
  const returnsStart = getRangeDateWindow(chartRange).startDate;
  const returnsCurrency = ownHeadline && !isPhone && (chartConvert || returnsStart)
    ? chartConvert?.target || account.currencies?.[0] || null
    : null;
  const returns = useHoldingReturns(returnsCurrency, returnsStart, allAccounts);
  const accountReturn = returns && account
    ? (holdingView ? returns.holdings.get(account.id) : returns.brokers.get(account.id)) || null
    : null;
  const rangeLabel = returnRangeLabel(chartRange, t);
  // What a holding cost in the display currency, each buy at its own day's
  // rate: the page's since-purchase returns, whatever range the chart is on.
  const paid = holdingView && chartConvert && paidReturns?.currency === chartConvert.target
    ? paidReturns.holdings.get(account.id) || null
    : null;
  let chartCaption = null;
  if (showsHoldings) chartCaption = t('sub.chartAtMarket');
  else if (holdingView) chartCaption = t('sub.chartInvested');

  // Desktop: the account's name, type and statement date share the balance's
  // line, over a chart drawn on the page, and the holding, broker, person or
  // loan detail follows the chart. The phone sheet keeps them stacked.
  const inlineHeader = !isPhone;
  const currencyChips = (
    accountCurrencies.length > 1 && currencyTab === 'all' && !chartConvert && (
      <Flex gap={1} mb={3}>
        {accountCurrencies.map(curr => {
          const isActive = (chartCurrency || accountCurrencies[0]) === curr;
          return (
            <Box
              key={curr}
              as="button"
              px={3}
              py="4px"
              fontSize={isPhone ? '13px' : 'xs'}
              fontWeight={600}
              borderRadius="full"
              bg={isActive ? 'redesign.heading' : 'redesign.bg.card'}
              color={isActive ? 'redesign.bg.surface' : 'redesign.text.muted'}
              _hover={{ bg: isActive ? 'redesign.text' : 'redesign.bg.hover' }}
              transition="all 0.15s"
              onClick={() => setChartCurrency(curr)}
            >
              {curr}
            </Box>
          );
        })}
      </Flex>
    )
  );
  const statement = statementLine(freshness, t);
  let typeLabel = '';
  if (account?.account_type) typeLabel = t(`types.${account.account_type}`, { defaultValue: account.account_group });
  else if (account?.account_group) typeLabel = t(`categories.${account.account_group}`, { defaultValue: account.account_group });
  // Copilot's header: the logo, the account's number in ink, then what it is
  // and how current, in italic; the name below, large.
  const mask = account?.external_account_number && String(account.external_account_number).slice(-4);
  const panelHeaderTop = account && (
    <Flex align="center" gap="10px" minW={0}>
      <Flex
        w="24px"
        h="24px"
        borderRadius="full"
        bg="redesign.bg.card"
        align="center"
        justify="center"
        flexShrink={0}
        overflow="hidden"
      >
        {/* MI-345: the institution's logo first, as the row shows it; the
            account's own icon is for one with no institution. */}
        {account.source_logo_url ? (
          <Image
            src={account.source_logo_url}
            alt=""
            boxSize="16px"
            objectFit="contain"
            fallback={<Text fontSize="sm" lineHeight="1">{account.icon || '🏦'}</Text>}
          />
        ) : (
          <Text fontSize="sm" lineHeight="1">{account.icon || '🏦'}</Text>
        )}
      </Flex>
      {mask && <Text fontSize="14px" fontWeight={600} color="redesign.heading" flexShrink={0}>{mask}</Text>}
      <Text fontSize="14px" fontStyle="italic" color="redesign.text.muted" isTruncated>
        {typeLabel}
        {statement && (
          <Text as="span" color={statement.late ? 'redesign.text.warn' : undefined}>{` · ${statement.text}`}</Text>
        )}
      </Text>
    </Flex>
  );
  // A broker or holding heads as every account does, Copilot's way: its
  // return on the account's line, its value beside the name, what it is made
  // of under it. Null for a broker in several currencies with none picked.
  const headline = !inlineHeader || !account ? null
    : showsHoldings
      ? brokerHeadline({
        account, accounts: allAccounts, cash: brokerCash, convert: chartConvert,
        ret: accountReturn, retCurrency: returns?.currency, rangeLabel, t,
      })
      : holdingView
        ? holdingHeadline({ account, convert: chartConvert, ret: accountReturn, rangeLabel, paid, t })
        : null;
  const headlineFigure = headline && (
    <Flex align="baseline" gap={1}>
      {headline.figure.approx && <Text as="span" color="redesign.text.muted" fontSize="sm">≈</Text>}
      <Figure size="card" code={headline.figure.code} amount={headline.figure.amount} />
    </Flex>
  );
  const panelHeader = account && (
    <Box>
      <Text fontSize="24px" fontWeight={600} color="redesign.heading" noOfLines={2} lineHeight="30px" letterSpacing="-0.01em">
        {account.name}
      </Text>
      {account.note && (
        <Text fontSize="sm" color="redesign.text.muted" noOfLines={1} mt={1}>{shortNote(account.note)}</Text>
      )}
      {currencyChips && <Box mt={3}>{currencyChips}</Box>}
      {!headline && showsHoldings && (
        <Box mt={4}>
          <BrokerSummary
            account={account} accounts={allAccounts} cash={brokerCash} convert={chartConvert}
            ret={accountReturn} retCurrency={returns?.currency} rangeLabel={rangeLabel}
          />
        </Box>
      )}
      {!headline && holdingView && (
        <Box mt={4}>
          <HoldingSummary account={account} convert={chartConvert} ret={accountReturn} rangeLabel={rangeLabel} paid={paid} />
        </Box>
      )}
    </Box>
  );

  const renderViewMode = () => (
    <Box flex={1} overflowY="auto" overflowX="hidden">
      {/* Identity (phone; the desktop draws it beside the balance) */}
      {!inlineHeader && (
      <Box px={px} pt={isPhone ? 2 : 5} pb={4}>
        <Flex align="flex-start" gap={3} mb={3}>
          {account?.source_logo_url ? (
            <Image
              src={account.source_logo_url}
              alt=""
              boxSize="36px"
              borderRadius="lg"
              objectFit="contain"
              flexShrink={0}
              fallback={account.icon ? <Text fontSize="2xl" lineHeight="1">{account.icon}</Text> : undefined}
            />
          ) : account?.icon && (
            <Text fontSize="2xl" lineHeight="1">{account.icon}</Text>
          )}
          <Box flex={1} minW={0}>
            <Text fontSize="xl" fontWeight="bold" color="redesign.heading" noOfLines={1}>
              {account?.name}
            </Text>
            <Flex gap={2} mt={1} flexWrap="wrap" align="center">
              <Tag
                size="sm"
                bg="redesign.bg.card"
                color="redesign.text"
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1}
                {...(isPhone ? { fontSize: '13px' } : {})}
              >
                {account?.account_type
                  ? t(`types.${account.account_type}`, { defaultValue: account.account_group })
                  : t(`categories.${account?.account_group}`, { defaultValue: account?.account_group })}
              </Tag>
            </Flex>
          </Box>
        </Flex>
        {account?.note && (
          <Text fontSize="sm" color="redesign.text" noOfLines={1} mt={2}>
            {shortNote(account.note)}
          </Text>
        )}
      </Box>
      )}

      {!inlineHeader && (
        <Box px={px}>
          {holdingView ? <HoldingSummary account={account} phone={isPhone} /> : renderSubAccount()}
          {showsHoldings && <BrokerSummary account={account} accounts={allAccounts} cash={brokerCash} phone={isPhone} convert={chartConvert} />}
        </Box>
      )}

      {/* Balance chart with currency selector when account has multiple currencies */}
      <Box px={px} pt={inlineHeader ? 5 : 0} pb={2} sx={isPhone ? PHONE_TARGETS : undefined}>
        {!inlineHeader && currencyChips}
        <AccountBalanceChart
          account={account}
          buckets={buckets}
          currency={chartCurrency || accountCurrencies[0] || currency}
          roi={ownHeadline ? undefined : roi}
          convert={chartConvert}
          phone={isPhone}
          memberIds={chartMembers}
          atMarket={atMarket}
          headline={!ownHeadline}
          caption={chartCaption}
          header={inlineHeader ? panelHeader : null}
          headerTop={inlineHeader ? panelHeaderTop : null}
          frameless={inlineHeader}
          onRangeChange={setChartRange}
          headlinePills={headline?.pills}
          headlineFigure={headlineFigure}
          headerFoot={headline?.lines.map((line) => (
            // What the value is made of, under the header at its full width.
            <Text key={line} fontSize="13px" color="redesign.text.muted" mt={1}>{line}</Text>
          ))}
        />
      </Box>

      {(inlineHeader || holdingView) && <Box px={px}>{renderSubAccount()}</Box>}

      {isBroker && (
        <Box px={px}>
          <BrokerDetail {...subAccountProps} cash={brokerCash} convert={chartConvert} returns={returns} />
        </Box>
      )}

      {isPhone && <PhoneAccountSummary account={account} accountId={accountId} reconciliation={reconciliation} />}

      {/* Movements — always full history of the account, ignoring overview
          range and currency filters (the chart up top has its own range). */}
      {isPhone ? (
        <PhoneAccountMovements account={account} />
      ) : (
      <Box px={{ base: 3, md: 5 }} pb={5}>
        {/* Copilot's rule between the chart and the movements. */}
        <Box h="1px" bg="redesign.border" mt={3} mb="6px" />
        <MovementsList
          filters={movementsFilters}
          dateRange={null}
          currency="all"
          contextAccountId={accountId}
          categories={categories}
          allTags={allTags}
          onMovementClick={setSelectedMovementId}
          refreshKey={movementsRefreshKey}
        />
      </Box>
      )}
    </Box>
  );

  return (
    <Box h="100%" bg="redesign.bg.surface" display="flex" flexDirection="column" position="relative">
      {selectedMovementId && (
        <Box position="absolute" inset={0} bg="redesign.bg.surface" zIndex={2} display="flex" flexDirection="column">
          <MovementDetailPanel
            movementId={selectedMovementId}
            onClose={handleCloseOverlay}
            onMovementUpdated={handleOverlayMovementUpdated}
            onMovementDeleted={handleOverlayMovementUpdated}
            onSelectMovement={setSelectedMovementId}
          />
        </Box>
      )}

      {/* Header. The phone sheet draws its own title and close. */}
      {!isPhone && (
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 2, md: 4 }}
        py={{ base: 2, md: 3 }}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="redesign.heading">
          {isCreateMode ? t('detail.headerNew') : showEditForm ? t('detail.headerEdit') : t('detail.header')}
        </Text>
        <Flex gap={2} align="center">
          {!isCreateMode && !showEditForm && account && (
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<LuChevronRight />}
              onClick={() => openAccountMovements(account.id)}
            >
              {t('detail.seeInMovements')}
            </Button>
          )}
          {!isCreateMode && !showEditForm && onAccountUpdated && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowEditForm(true)}
            >
              {t('detail.edit')}
            </Button>
          )}
          <IconButton
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label={t('detail.closeAria')}
          />
        </Flex>
      </Flex>
      )}

      {/* Content */}
      {loading ? (
        <Flex justify="center" align="center" flex={1}>
          <Spinner size="xl" />
        </Flex>
      ) : error ? (
        <Text color="redesign.accent.red" textAlign="center" py={12}>{error}</Text>
      ) : showEditForm || isCreateMode ? (
        <Box flex={1} overflowY="auto" overflowX="hidden">
          <AccountForm
            account={isCreateMode ? null : account}
            onSaved={handleFormSaved}
            onCancel={isCreateMode ? undefined : () => setShowEditForm(false)}
          />
        </Box>
      ) : (
        account && renderViewMode()
      )}
    </Box>
  );
}
